import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categories } from '@/mocks/articles';
import type { Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<Article | null>(() => articles.find((a) => a.id === id) || articles[0] || null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);

  useEffect(() => {
    const found = articles.find((a) => a.id === id);
    setArticle(found || articles[0] || null);

    if (found) {
      const related = articles
        .filter((a) => a.id !== found.id && a.categorySlug === found.categorySlug)
        .slice(0, 3);
      setRelatedArticles(related);
    }

    window.scrollTo(0, 0);
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-4">
              <i className="ri-emotion-sad-line text-2xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-5">你访问的文章不存在或已被删除</p>
            <Link to="/" className="inline-block px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabel = categories.find((c) => c.slug === article.categorySlug)?.name || article.category;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '51大赛吃瓜黑料网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://ahljjx.cn/article/${article.id}`,
    },
  };

  return (
    <>
      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { label: categoryLabel, href: '/' },
            { label: article.title },
          ]}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
        />

        <section className="relative w-full h-[320px] md:h-[450px] flex items-center overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/15 to-black/55"></div>

          <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 md:px-6 mt-auto pb-8 md:pb-12">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-2.5 py-1 rounded-md text-xs font-bold bg-primary-500 text-white whitespace-nowrap">
                  {categoryLabel}
                </span>
                {article.isHot && (
                  <span className="px-2 py-1 rounded-md text-xs font-bold bg-accent-500 text-background-50 flex items-center gap-1 whitespace-nowrap">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-fire-line text-xs"></i>
                    </div>
                    HOT
                  </span>
                )}
              </div>
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-black text-white leading-tight">
                {article.title}
              </h1>
            </div>
          </div>
        </section>

        <article className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="lg:w-[65%]">
              <div className="flex items-center gap-4 mb-8 pb-6 border-b border-background-200/70 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center">
                    <i className="ri-user-line text-foreground-600 text-xs"></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700">{article.author}</span>
                </div>
                <span className="text-foreground-300">·</span>
                <span className="text-sm text-foreground-500">{article.publishDate}</span>
                <span className="text-foreground-300">·</span>
                <span className="text-sm text-foreground-500 flex items-center gap-1">
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-eye-line text-xs"></i>
                  </div>
                  {article.viewCount >= 10000
                    ? `${(article.viewCount / 10000).toFixed(0)}万 阅读`
                    : `${article.viewCount} 阅读`}
                </span>
              </div>

              <div
                className="article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex flex-wrap items-center gap-2 mt-10 pt-6 border-t border-background-200/70">
                <span className="text-xs text-foreground-400 mr-1">标签：</span>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to="/"
                    className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>

            <aside className="lg:w-[35%]">
              <div className="bg-background-100 rounded-2xl p-5 md:p-6 sticky top-24">
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-fire-line text-primary-500"></i>
                  </div>
                  相关爆料
                </h3>

                {relatedArticles.length > 0 ? (
                  <div className="space-y-5">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to="/"
                        className="flex gap-3 group cursor-pointer"
                      >
                        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={ra.coverImage}
                            alt={ra.title}
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-bold text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                            {ra.title}
                          </h4>
                          <p className="text-xs text-foreground-400 mt-1">{ra.publishDate}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground-400">暂无相关文章</p>
                )}
              </div>
            </aside>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}